import { useCalendarContext } from "@/context/calendar";
import { Order, OrderItem } from "@/types/order";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { formatPrice } from "@/utils/helpers";

export type OrderItemWithCurrency = OrderItem & { currency_code: string }; 

const useItems = (orders: Order[]) => {
  const {
    date: { from, to },
  } = useCalendarContext();

  const items = useMemo(() => {
    return orders.flatMap((order) =>
      order.items.map((item) => ({
        ...item,
        currency_code: order.currency_code,
      }))
    );
  }, [orders]);

  const columns: ColumnDef<OrderItemWithCurrency>[] = [
    {
      accessorKey: "title",
      header: "Name",
      cell: ({ row }) => {
        const title = row.original.title;
        const trimmedTitle = title.replace(/\b\d+\s*ml\b/i, "").trim();
        return <p>{trimmedTitle}</p>;
      },
    },
    {
      accessorKey: "variant_title",
      header: "Variant",
      cell: ({ row }) => {
        const variantTitle = row.original.variant_title || "-";
        return <p>{variantTitle}</p>;
      },
    },
    {
      accessorKey: "unit_price",
      header: "Retail Price",
      cell: ({ row }) => {
        const unitPrice = row.original.unit_price;
        const currency = row.original.currency_code;
        return <p>{formatPrice(unitPrice, currency)}</p>;
      },
    },
    {
      accessorKey: "quantity",
      header: "Quantity",
      cell: ({ row }) => {
        const quantity = row.original.quantity;
        return <p>{quantity}</p>;
      },
    },
    {
      accessorKey: "variant_sku",
      header: "Variant SKU",
      cell: ({ row }) => {
        const variantSKU = row.original.variant_sku || "-";
        return <p>{variantSKU}</p>;
      },
    },
    {
      accessorKey: "vintage",
      header: "Vintage",
      cell: ({ row }) => {
        const vintage = row.original.metadata?.vintage || "-";
        return <p>{vintage}</p>;
      },
    },
  ];

  const filteredItems = useMemo(() => {
    const filtered = items.filter((item) => {
      const created = new Date(item.created_at).getTime();

      if (!from && !to) {
        return true;
      }

      if (from && !to) {
        const fromStart = new Date(from);
        fromStart.setHours(0, 0, 0, 0);
        const fromEnd = new Date(from);
        fromEnd.setHours(23, 59, 59, 999);
        return created >= fromStart.getTime() && created <= fromEnd.getTime();
      }

      if (from && to) {
        const fromTime = new Date(from);
        fromTime.setHours(0, 0, 0, 0);
        const toTime = new Date(to);
        toTime.setHours(23, 59, 59, 999);
        return created >= fromTime.getTime() && created <= toTime.getTime();
      }

      return true;
    });

    return filtered.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [items, from, to]);

  return { columns, filteredItems };
};

export { useItems };
